/**
 * VENNER — Add-ons
 *
 *  · The initials field appears when the customer picks an engraving variant.
 *  · Custom variant picker that shows the variant's color, instead of the browser's native dropdown.
 *  · If the variant asks for initials and the field is empty, the customer is warned before adding to cart.
 */
/*
  The snippet includes this file once per section, so on a page with several (cart, drawer, product page)
  the browser runs it several times. Without this guard the listeners got duplicated and each click opened
  and closed the dropdown in the same gesture: it looked like nothing happened.
*/
if (!window.__vennerCrossSellReady) {
  window.__vennerCrossSellReady = true;

  const normalize = (text) => (text || '').trim().toLowerCase();

  function needsInitials(card, variantTitle) {
    const triggers = (card.dataset.vxsTriggers || 'with engraving')
      .split(',')
      .map(normalize)
      .filter(Boolean);

    return triggers.some((trigger) => normalize(variantTitle).includes(trigger));
  }

  /** Shows or hides the initials field based on the selected variant. */
  function syncInitials(card, variantTitle) {
    const field = card.querySelector('[data-vxs-initials]');

    if (!field) return;

    const visible = needsInitials(card, variantTitle);
    const input = field.querySelector('input');

    field.toggleAttribute('hidden', !visible);

    if (input) {
      input.disabled = !visible;
      // `required` lets Prestige's form block the submit too, not just our listener.
      input.required = visible;
      if (!visible) {
        input.value = '';
        clearError(card);
      }
    }
  }

  function clearError(card) {
    const input = card.querySelector('.vxs__initials');
    const notice = card.querySelector('[data-vxs-initials-error]');

    input?.removeAttribute('aria-invalid');
    notice?.setAttribute('hidden', '');
  }

  function showError(card) {
    const input = card.querySelector('.vxs__initials');
    const notice = card.querySelector('[data-vxs-initials-error]');

    input?.setAttribute('aria-invalid', 'true');
    notice?.removeAttribute('hidden');
    input?.focus();
  }

  /**
   * Only one card open at a time: with several open, the floating panels stacked on top of each other.
   * The panel is also repositioned if it would overflow the right edge of the screen.
   */
  function positionPanel(card) {
    const panel = card?.querySelector('.vxs__options');

    if (!panel) return;

    panel.style.insetInlineStart = '';
    panel.style.insetInlineEnd = '';

    const rect = panel.getBoundingClientRect();

    if (rect.right > window.innerWidth - 8) {
      panel.style.insetInlineStart = 'auto';
      panel.style.insetInlineEnd = '0';
    }
  }

  function keepOnlyOneOpen(sw) {
    sw.closest('.vxs__list')?.querySelectorAll('.vxs__switch-input').forEach((other) => {
      if (other !== sw && other.checked) other.checked = false;
    });

    requestAnimationFrame(() => positionPanel(sw.closest('[data-vxs-triggers]')));
  }

  document.addEventListener('change', (event) => {
    const sw = event.target.closest('.vxs__switch-input');

    if (sw && sw.checked) keepOnlyOneOpen(sw);
  });

  // The label fires `change`, but if some integration toggles it via code, the click still covers it.
  document.addEventListener('click', (event) => {
    const label = event.target.closest('.vxs__switch');

    if (!label) return;

    requestAnimationFrame(() => {
      const sw = label.closest('[data-vxs-triggers]')?.querySelector('.vxs__switch-input');
      if (sw?.checked) keepOnlyOneOpen(sw);
    });
  });

  window.addEventListener('resize', () => {
    document.querySelectorAll('.vxs__switch-input:checked').forEach((sw) => {
      positionPanel(sw.closest('[data-vxs-triggers]'));
    });
  });

  // ------------------------------------------------------------------ variant picker

  function closeAll(exceptPicker) {
    document.querySelectorAll('[data-vxs-picker]').forEach((picker) => {
      if (picker === exceptPicker) return;
      picker.querySelector('[data-vxs-picker-toggle]')?.setAttribute('aria-expanded', 'false');
      picker.querySelector('[data-vxs-picker-list]')?.setAttribute('hidden', '');
    });
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-vxs-picker-toggle]');
    const option = event.target.closest('[data-vxs-picker-option]');

    if (toggle) {
      const picker = toggle.closest('[data-vxs-picker]');
      const list = picker.querySelector('[data-vxs-picker-list]');
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';

      closeAll(picker);
      toggle.setAttribute('aria-expanded', String(!isOpen));
      list.toggleAttribute('hidden', isOpen);
      return;
    }

    if (option) {
      const picker = option.closest('[data-vxs-picker]');
      const card = option.closest('[data-vxs-triggers]');
      const input = card.querySelector('[data-vxs-variant-input]');
      const current = picker.querySelector('[data-vxs-picker-current]');

      input.value = option.value;
      current.innerHTML = option.innerHTML;

      picker.querySelectorAll('[data-vxs-picker-option]').forEach((o) => {
        o.setAttribute('aria-selected', String(o === option));
      });

      closeAll(null);
      syncInitials(card, option.textContent);
      return;
    }

    closeAll(null);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll(null);
  });

  // Initials are always uppercase, just like on the product page.
  document.addEventListener('input', (event) => {
    const input = event.target.closest('.vxs__initials');

    if (!input) return;

    const cursor = input.selectionStart;
    const upper = input.value.toUpperCase();

    if (upper !== input.value) {
      input.value = upper;
      input.setSelectionRange(cursor, cursor);
    }

    if (input.value.trim()) clearError(input.closest('[data-vxs-triggers]'));
  });

  /*
    Validation runs in the capture phase to get ahead of Prestige's <product-form>, which fires the cart
    request as soon as it receives the submit. Without this, someone could pay for engraving and send no initials.
  */
  // If native validation is what blocks the submit, our own notice is shown anyway.
  document.addEventListener('invalid', (event) => {
    const input = event.target.closest?.('.vxs__initials');

    if (!input) return;

    /*
      preventDefault on `invalid` suppresses the browser's tooltip ("Fill out this field"), which doesn't
      match the store's styling. The field stays invalid, so the form is still blocked, and the notice the
      customer sees is ours.
    */
    event.preventDefault();
    showError(input.closest('[data-vxs-triggers]'));
  }, { capture: true });

  document.addEventListener('submit', (event) => {
    const card = event.target.closest('[data-vxs-triggers]');

    if (!card) return;

    const field = card.querySelector('[data-vxs-initials]');
    const input = field?.querySelector('input');

    if (!field || field.hasAttribute('hidden') || !input || input.disabled) return;

    if (!input.value.trim()) {
      event.preventDefault();
      event.stopPropagation();
      showError(card);
    }
  }, { capture: true });
}
